import { useTranslation } from "react-i18next";
import { Avatar, Box, Chip, Typography } from "@mui/material";

import useIsOwn from "../../../../hooks/useIsOwn";

import { useUserDataFormatter } from "../../../User/hooks/useUserDataFormatter";

import { Service } from "../../types/service.type";
import ProgramBG from "../../assets/images/service-card-program-cover.png";
import { ServiceCardNoRateIcon, ServiceCardRateIcon, UserVerifiedIcon } from "../../assets/icons/go-service-icons";

import ServiceApprovalStatus from "../ServiceApprovalStatus";

const ProgramCardHeader = ({ service }: { service: Service }) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceCard" });
  const { t: tServiceType } = useTranslation("goService", { keyPrefix: "serviceType" });
  const { type, user, rating } = service;
  const { fullName, firstTwoChr, media, club, role, isVerified } = useUserDataFormatter(user);

  const { isOwn: isOwnService, isLoading: isOwnServiceLoading } = useIsOwn({
    userId: service?.user?.id,
    isUserDataLoading: false,
  });

  return (
    <Box sx={styles.container}>
      <Box sx={styles.coverContainer}>
        <Box component="img" src={ProgramBG} alt="service-cover" sx={styles.coverImage} />
        <Box sx={styles.statusContainer}>
          <Chip label={tServiceType(type)} sx={styles.serviceTypeChip} />
          {isOwnService && <ServiceApprovalStatus service={service} isLoading={isOwnServiceLoading} />}
        </Box>

        <Box sx={styles.avatarContainer}>
          <Avatar src={media} sx={styles.avatar}>
            {firstTwoChr}
          </Avatar>
          {isVerified && <UserVerifiedIcon sx={styles.verifiedIcon} />}
        </Box>
      </Box>
      <Box sx={styles.userInfoContainer}>
        <Box sx={styles.userInfo}>
          <Typography sx={styles.userName}>{fullName}</Typography>
          <Typography sx={styles.userRole}>
            {role} {club && `• ${club}`}
          </Typography>
        </Box>
        <Chip
          label={
            <Box sx={styles.ratingContainer}>
              {rating === 0 && (
                <>
                  <ServiceCardNoRateIcon />
                  {t("noRate")}
                </>
              )}
              {rating > 0 && (
                <>
                  <ServiceCardRateIcon />
                  {rating}
                </>
              )}
            </Box>
          }
          sx={styles.ratingChip(rating)}
        />
      </Box>
    </Box>
  );
};

export default ProgramCardHeader;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  coverContainer: {
    position: "relative",
    paddingBottom: "22px",
  },
  coverImage: {
    width: "100%",
    height: "114px",
    objectFit: "cover",
  },
  statusContainer: {
    position: "absolute",
    top: "16px",
    left: "16px",
    display: "flex",
    gap: "8px",
  },
  serviceTypeChip: {
    color: "#EFA81A",
    fontFamily: "Montserrat",
    fontSize: "13px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    height: "fit-content",
    padding: "3px 8px",
    border: `1px solid #EFA81A`,
    background: "#FFF",
    "& .MuiChip-label": {
      padding: "0px",
    },
  },
  menuButton: {
    position: "absolute",
    top: "16px",
    right: "16px",
    backgroundColor: "#FFF !important",
    borderRadius: "6px",
  },
  avatarContainer: {
    position: "absolute",
    top: "82px",
    left: "16px",
  },
  avatar: {
    width: "56px",
    height: "56px",
    border: "3px solid #FFF",
    position: "relative",
  },
  verifiedIcon: {
    position: "absolute",
    bottom: "-6px",
    left: "28px",
    transform: "translateX(-50%)",
  },
  userInfoContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0 16px",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  userName: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "24px",
  },
  userRole: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "18px",
  },
  ratingContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  ratingChip: (rating: number) => ({
    fontFamily: "Montserrat",
    color: "var(--Dark---800, #414141)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    background: rating === 0 ? "#E7EFED" : "var(--Yellow---25, #FEF8ED)",
    height: "fit-content",
    padding: "4px 8px",
    "& .MuiChip-label": {
      padding: "0px",
    },
  }),
};
